import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Plus, FileText, FolderOpen } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface KnowledgeArticle {
  id: string;
  category_id: string | null;
  company_id: string | null;
  category?: string | null; // optional free-text category if column exists
  title: string;
  content: string;
  summary: string | null;
  keywords: string[]; // optional schema; safe to keep as any[]
  tags: string[];     // optional schema; safe to keep as any[]
  article_type: string;
  priority: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  knowledge_categories?: {
    name: string;
  };
}

interface KnowledgeCategory {
  id: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
}

interface Company {
  id: string;
  name: string;
}

export default function KnowledgeBasePage() {
  const { theme } = useTheme();
  const [articles, setArticles] = useState<KnowledgeArticle[]>([]);
  const [categories, setCategories] = useState<KnowledgeCategory[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [autoImported, setAutoImported] = useState(false);

  // ElevenLabs KB controls
  const [agentId, setAgentId] = useState('');
  const [kbUrl, setKbUrl] = useState('');
  const [docId, setDocId] = useState('');
  const [kbBusy, setKbBusy] = useState(false);
  const [textName, setTextName] = useState('');
  const [textText, setTextText] = useState('');
  const [kbStatus, setKbStatus] = useState<any>(null);
  const [agentKbSize, setAgentKbSize] = useState<any>(null);
  const [agentDocs, setAgentDocs] = useState<any[]>([]);
  const [polling, setPolling] = useState(false);
  const [autoAgentId, setAutoAgentId] = useState(false);
  const [autoPoll, setAutoPoll] = useState(true);
  const [kbLastUpdated, setKbLastUpdated] = useState<number>(0);

  // ElevenLabs health (for guardrails)
  const [health, setHealth] = useState<any>(null);
  const [convaiWrite, setConvaiWrite] = useState<boolean | null>(null);
  const [healthLoading, setHealthLoading] = useState<boolean>(true);

  useEffect(() => {
    loadData();
  }, []);

  // Fetch ElevenLabs health to gate attach/detach when missing permissions
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('elevenlabs-health', { body: {} });
        if (!mounted) return;
        if (error) {
          console.error('elevenlabs-health error:', error);
          setHealth(null);
          setConvaiWrite(false);
        } else {
          setHealth(data);
          setConvaiWrite(Boolean((data as any)?.convai_write));
        }
      } catch (e) {
        console.warn('elevenlabs-health fetch failed:', e);
        if (mounted) {
          setHealth(null);
          setConvaiWrite(false);
        }
      } finally {
        if (mounted) setHealthLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Auto-discover ElevenLabs agent id from company_configs if not provided
  useEffect(() => {
    if (agentId) return;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('company_configs')
          .select('elevenlabs_agent_id, agent_id')
          .eq('is_active', true)
          .limit(1)
          .maybeSingle();
        if (!error) {
          const discovered = (data as any)?.elevenlabs_agent_id || (data as any)?.agent_id;
          if (discovered) {
            setAgentId(discovered);
            setAutoAgentId(true);
          }
        }
      } catch (e) {
        console.warn('Agent ID discovery failed:', e);
      }
    })();
  }, [agentId]);

  // Background polling of agent KB size and documents when agentId is set
  useEffect(() => {
    if (!agentId || !autoPoll) return;
    let cancelled = false;

    const tick = async () => {
      const sizeRes = await elInvokeSilent('agent_kb_size', { agent_id: agentId });
      if (!cancelled && sizeRes?.size) setAgentKbSize(sizeRes.size);

      const listRes = await elInvokeSilent('list_agent_kb', { agent_id: agentId });
      if (!cancelled) {
        const docs = Array.isArray(listRes?.documents)
          ? listRes.documents
          : (listRes?.documents?.documents || []);
        setAgentDocs(docs || []);
      }
      setKbLastUpdated(Date.now());
    };

    // initial fetch + interval
    tick();
    const id = setInterval(tick, 30000);
    return () => {
      cancelled = true;
      clearInterval(id as any);
    };
  }, [agentId, autoPoll]);

  async function loadData() {
    try {
      // Articles (no FK join to avoid missing relationship error)
      // Try order by priority then fallback to created_at-only if priority column is missing
      let articlesData: any[] | null = null;

      const attempt1 = await supabase
        .from('knowledge_articles')
        .select('*')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (attempt1.error) {
        console.warn('Articles query failed (priority may not exist), falling back:', attempt1.error);
        const attempt2 = await supabase
          .from('knowledge_articles')
          .select('*')
          .order('created_at', { ascending: false });

        if (attempt2.error) {
          console.error('Articles fallback query failed:', attempt2.error);
          alert(`Error loading articles: ${attempt2.error.message || 'Unknown error'}`);
          throw attempt2.error;
        }
        articlesData = attempt2.data || [];
      } else {
        articlesData = attempt1.data || [];
      }

      // Categories are optional; warn if missing
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('knowledge_categories')
        .select('*')
        .order('sort_order');

      if (categoriesError) {
        console.warn('Categories query failed (optional):', categoriesError);
      }

      // Companies (optional, informational)
      const { data: companiesData, error: companiesError } = await supabase
        .from('companies')
        .select('id, name')
        .order('name');

      if (companiesError) {
        console.warn('Companies query failed (optional):', companiesError);
      }

      setArticles(articlesData || []);
      setCategories(categoriesData || []);
      setCompanies(companiesData || []);
    } catch (error) {
      console.error('Error loading knowledge base:', error);
      alert(`Error loading knowledge base: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }

  // Auto-import FAQs once when knowledge_articles has none but agent_faqs has active entries
  useEffect(() => {
    (async () => {
      if (autoImported) return;
      try {
        // Try to count FAQs by article_type if column exists; fallback to total count
        let faqCount = 0;
        const faqAttempt = await supabase
          .from('knowledge_articles')
          .select('id', { count: 'exact', head: true })
          .eq('article_type', 'faq');

        if (faqAttempt.error) {
          console.warn('FAQ count query by article_type failed; falling back to total count:', faqAttempt.error);
          const totalAttempt = await supabase
            .from('knowledge_articles')
            .select('id', { count: 'exact', head: true });
          if (totalAttempt.error) {
            console.warn('Total count query failed (knowledge_articles)', totalAttempt.error);
            return;
          }
          faqCount = totalAttempt.count || 0;
        } else {
          faqCount = faqAttempt.count || 0;
        }
        const { count: agentFaqCount, error: agentFaqErr } = await supabase
          .from('agent_faqs')
          .select('id', { count: 'exact', head: true })
          .eq('is_active', true);
        if (agentFaqErr) {
          console.warn('Agent FAQ count query failed', agentFaqErr);
          return;
        }
        if ((faqCount || 0) === 0 && (agentFaqCount || 0) > 0) {
          setAutoImported(true);
          await handleImportFaqs();
        }
      } catch (e) {
        console.warn('Auto import check failed', e);
      }
    })();
  }, [autoImported]);

  // Import active FAQs from legacy agent_faqs into unified knowledge_articles as article_type="faq"
  async function handleImportFaqs() {
    setImporting(true);
    try {
      const { data: faqs, error } = await supabase
        .from('agent_faqs')
        .select('*')
        .eq('is_active', true);

      if (error) throw error;

      const summarize = (text: string, len = 240) => {
        if (!text) return '';
        const t = String(text);
        return t.length > len ? t.slice(0, len) + '…' : t;
      };

      // Build payloads conservatively to tolerate schema variance
      const payloads = (faqs || []).map((f: any) => ({
        title: f.question,
        content: f.answer,
        article_type: 'faq',
        // Include tags if column exists; DB will ignore unknown keys if schema mismatch is handled server-side,
        // but to be safer we keep fields common across environments.
        tags: Array.isArray(f.keywords) ? f.keywords : [],
        priority: typeof f.priority === 'number' ? f.priority : 5,
        is_active: f.is_active !== false,
        company_id: f.company_config_id || null,
        summary: summarize(f.answer),
      }));

      // Try bulk upsert on (company_id,title). If unique index doesn't exist, fallback per-row insert.
      const { error: upsertErr } = await supabase
        .from('knowledge_articles')
        .upsert(payloads, { onConflict: 'company_id,title' });

      if (upsertErr) {
        console.warn('Bulk upsert failed; falling back to inserts.', upsertErr);
        for (const p of payloads) {
          try {
            await supabase.from('knowledge_articles').insert(p);
          } catch (e) {
            console.error('Insert failed for:', p.title, e);
          }
        }
      }

      alert(`Imported ${payloads.length} FAQs into knowledge_articles (article_type="faq").`);
      await loadData();
    } catch (e) {
      console.error('Import FAQs error', e);
      alert(`Failed to import FAQs: ${e instanceof Error ? e.message : 'Unknown error'}`);
    } finally {
      setImporting(false);
    }
  }

  // ElevenLabs KB helpers
  async function elInvoke(action: string, params: Record<string, any>) {
    setKbBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('elevenlabs-knowledge-manager', {
        body: { action, ...params },
      });
      if (error) throw error;
      return data;
    } catch (e: any) {
      console.error(`[EL KB] ${action} failed:`, e);
      alert(`${action} failed: ${e?.message || 'Unknown error'}`);
      return null;
    } finally {
      setKbBusy(false);
    }
  }
  
  // Silent invoke that logs errors and does not alert or toggle busy state.
  async function elInvokeSilent(action: string, params: Record<string, any>) {
    try {
      const { data, error } = await supabase.functions.invoke('elevenlabs-knowledge-manager', {
        body: { action, ...params },
      });
      if (error) throw error;
      return data;
    } catch (e: any) {
      console.warn(`[EL KB] ${action} (silent) failed:`, e?.message || e);
      return null;
    }
  }

  async function handleCreateUrlDoc() {
    if (!kbUrl) {
      alert('Enter a URL to create a KB doc.');
      return;
    }
    const res = await elInvoke('create_url_doc', { url: kbUrl });
    if (res?.document) {
      alert(`Created KB document. ID: ${res.document?.documentation_id || JSON.stringify(res.document)}`);
    }
  }

  async function handleCreateTextDoc() {
    if (!textName || !textText) {
      alert('Enter both Text Doc Name and Text Body.');
      return;
    }
    const res = await elInvoke('create_text_doc', { name: textName, text: textText });
    if (res?.document) {
      alert(`Created Text KB document. ID: ${res.document?.documentation_id || JSON.stringify(res.document)}`);
    }
  }

  async function handleComputeIndex() {
    if (!docId) {
      alert('Enter documentation_id to compute index.');
      return;
    }
    const res = await elInvoke('compute_index', { documentation_id: docId });
    if (res?.index) {
      alert('Index compute triggered.');
    }
  }

  async function handleRagStatus() {
    if (!docId) {
      alert('Enter documentation_id to check status.');
      return;
    }
    const res = await elInvoke('rag_status', { documentation_id: docId });
    if (res?.status) {
      setKbStatus(res.status);
      alert(`Status: ${JSON.stringify(res.status)}`);
    }
  }

  async function handleAttachDocs() {
    if (!agentId || !docId) {
      alert('Enter agent_id and documentation_ids (comma-separated).');
      return;
    }
    const ids = docId.split(',').map((s) => s.trim()).filter(Boolean);
    const res = await elInvoke('attach_to_agent', { agent_id: agentId, documentation_ids: ids });
    if (res?.success || res?.agent) {
      alert('Attached docs to agent.');
    }
  }

  async function handleAgentKbSize() {
    if (!agentId) {
      alert('Enter agent_id first.');
      return;
    }
    const res = await elInvoke('agent_kb_size', { agent_id: agentId });
    if (res?.size) {
      setAgentKbSize(res.size);
      alert(`Agent KB size: ${JSON.stringify(res.size)}`);
    }
  }

  async function handleListAgentKb() {
    if (!agentId) {
      alert('Enter agent_id first.');
      return;
    }
    const res = await elInvoke('list_agent_kb', { agent_id: agentId });
    if (res?.documents) {
      const docs = Array.isArray(res.documents) ? res.documents : (res.documents?.documents || []);
      setAgentDocs(docs || []);
      alert(`Listed ${docs?.length || 0} docs on agent.`);
    }
  }

  async function handleDetachDocs() {
    if (!agentId || !docId) {
      alert('Enter agent_id and documentation_ids (comma-separated).');
      return;
    }
    const ids = docId.split(',').map((s) => s.trim()).filter(Boolean);
    const res = await elInvoke('detach_from_agent', { agent_id: agentId, documentation_ids: ids });
    if (res?.success) {
      alert('Detached docs from agent.');
      await handleListAgentKb();
    }
  }

  async function handleComputeIndexAndPoll() {
    if (!docId) {
      alert('Enter documentation_id to compute index.');
      return;
    }
    setPolling(true);
    try {
      await elInvoke('compute_index', { documentation_id: docId });

      const maxAttempts = 20;
      const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

      for (let i = 0; i < maxAttempts; i++) {
        await delay(3000);
        const res = await elInvoke('rag_status', { documentation_id: docId });
        if (!res?.status) continue;
        setKbStatus(res.status);

        const s = res.status;
        const pages = s?.number_of_pages ?? s?.totals?.pages_total ?? s?.pages_total;
        const finished = (typeof pages === 'number' && pages > 0)
          || s?.status === 'indexed'
          || (Array.isArray(s?.indexes) && s.indexes.every((x: any) => x?.status === 'indexed'));

        if (finished) {
          alert('Indexing complete.');
          return;
        }
      }
      alert('Indexing poll timed out. Check status again later.');
    } finally {
      setPolling(false);
    }
  }

  const articlesByCategory = articles.reduce((acc, article) => {
    const categoryName =
      article.knowledge_categories?.name ||
      (article as any).category ||
      'Uncategorized';
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(article);
    return acc;
  }, {} as Record<string, KnowledgeArticle[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="animate-spin rounded-full h-8 w-8 border-b-2"
          style={{ borderColor: theme.colors.primaryColor }}
        />
      </div>
    );
  }

  return (
    <div className="p-6" style={{ backgroundColor: theme.colors.bgPage }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
            Knowledge Base
          </h1>
          <p style={{ color: theme.colors.textSecondary }}>
            Manage documents and FAQs for AI voice agents
          </p>
        </div>

        <div className="mt-3">
          <label className="block text-xs mb-1" style={{ color: theme.colors.textSecondary }}>
            Text Doc Name (create_text_doc)
          </label>
          <input
            type="text"
            value={textName}
            onChange={(e) => setTextName(e.target.value)}
            className="w-full px-2 py-1 rounded text-sm mb-2"
            style={{
              backgroundColor: theme.colors.bgPage,
              color: theme.colors.textPrimary,
              border: `1px solid ${theme.colors.borderColor}`,
            }}
            placeholder="e.g., Onboarding Script"
          />
          <label className="block text-xs mb-1" style={{ color: theme.colors.textSecondary }}>
            Text Body
          </label>
          <textarea
            value={textText}
            onChange={(e) => setTextText(e.target.value)}
            className="w-full px-2 py-1 rounded text-sm"
            rows={4}
            style={{
              backgroundColor: theme.colors.bgPage,
              color: theme.colors.textPrimary,
              border: `1px solid ${theme.colors.borderColor}`,
            }}
            placeholder="Paste the knowledge content..."
          />
        </div>
        <button
          onClick={handleImportFaqs}
          disabled={importing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          title={'Import active FAQs from Agent FAQs into knowledge_articles as article_type="faq"'}
        >
          <Plus className="w-4 h-4" />
          {importing ? 'Importing FAQs…' : 'Import FAQs from Agent FAQs'}
        </button>
      </div>

      <div
        className="mb-6 p-4 rounded-lg"
        style={{
          backgroundColor: theme.colors.bgCard,
          border: `1px solid ${theme.colors.borderColor}`,
        }}
      >
        <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
          About this page: Centralized knowledge repository backed by the{' '}
          <span className="font-mono">knowledge_articles</span> table. Articles may be seeded from{' '}
          <span className="font-mono">inbound-scripts-knowledge.md</span> and{' '}
          <span className="font-mono">outbound-scripts.md</span> or imported from legacy Agent FAQs. If a category isn’t assigned, items will appear under “Uncategorized”.
        </p>
        <p className="text-xs mt-2" style={{ color: theme.colors.textTertiary }}>
          Note: Legacy tables <span className="font-mono">general_knowledge_base</span> and{' '}
          <span className="font-mono">agent_knowledge_base</span> are maintained for backward compatibility. Prefer creating and curating content in{' '}
          <span className="font-mono">knowledge_articles</span> going forward.
        </p>
      </div>

      {/* ElevenLabs convai_write notice */}
      {convaiWrite === false && (
        <div className="mb-4 p-3 rounded-lg border" style={{ backgroundColor: '#FEF3C7', borderColor: '#F59E0B', color: '#92400E' }}>
          <p className="text-sm">
            ElevenLabs API key is missing convai_write permission. Attaching/detaching docs to agents is disabled until updated.
          </p>
        </div>
      )}
      {/* ElevenLabs Knowledge Manager controls */}
      <div
        className="mb-6 p-4 rounded-lg"
        style={{
          backgroundColor: theme.colors.bgCard,
          border: `1px solid ${theme.colors.borderColor}`,
        }}
      >
        <h2 className="text-lg font-semibold mb-2" style={{ color: theme.colors.textPrimary }}>
          ElevenLabs Knowledge Manager
        </h2>
        <p className="text-sm mb-3" style={{ color: theme.colors.textSecondary }}>
          Create and index KB docs via the server-side gateway. Attach documentation_ids to an agent and check KB size.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs mb-1" style={{ color: theme.colors.textSecondary }}>
              Agent ID
            </label>
            <input
              type="text"
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className="w-full px-2 py-1 rounded text-sm"
              style={{
                backgroundColor: theme.colors.bgPage,
                color: theme.colors.textPrimary,
                border: `1px solid ${theme.colors.borderColor}`,
              }}
              placeholder="elevenlabs agent id"
            />
          </div>

          <div>
            <label className="block text-xs mb-1" style={{ color: theme.colors.textSecondary }}>
              KB URL (create_url_doc)
            </label>
            <input
              type="text"
              value={kbUrl}
              onChange={(e) => setKbUrl(e.target.value)}
              className="w-full px-2 py-1 rounded text-sm"
              style={{
                backgroundColor: theme.colors.bgPage,
                color: theme.colors.textPrimary,
                border: `1px solid ${theme.colors.borderColor}`,
              }}
              placeholder="https://yourdomain.com/faq-or-policy"
            />
          </div>

          <div>
            <label className="block text-xs mb-1" style={{ color: theme.colors.textSecondary }}>
              documentation_id(s)
            </label>
            <input
              type="text"
              value={docId}
              onChange={(e) => setDocId(e.target.value)}
              className="w-full px-2 py-1 rounded text-sm"
              style={{
                backgroundColor: theme.colors.bgPage,
                color: theme.colors.textPrimary,
                border: `1px solid ${theme.colors.borderColor}`,
              }}
              placeholder="doc_abc123 or doc_1,doc_2"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-3">
          <button
            onClick={handleCreateUrlDoc}
            disabled={kbBusy}
            className="px-3 py-1 rounded text-white text-sm"
            style={{ backgroundColor: theme.colors.primaryColor }}
          >
            Create URL Doc
          </button>
          <button
            onClick={handleCreateTextDoc}
            disabled={kbBusy}
            className="px-3 py-1 rounded text-white text-sm"
            style={{ backgroundColor: theme.colors.primaryColor }}
          >
            Create Text Doc
          </button>
          <button
            onClick={handleComputeIndex}
            disabled={kbBusy}
            className="px-3 py-1 rounded text-white text-sm"
            style={{ backgroundColor: theme.colors.primaryColor }}
          >
            Compute Index
          </button>
          <button
            onClick={handleComputeIndexAndPoll}
            disabled={kbBusy || polling}
            className="px-3 py-1 rounded text-white text-sm"
            style={{ backgroundColor: theme.colors.primaryColor }}
          >
            {polling ? 'Polling…' : 'Compute + Poll'}
          </button>
          <button
            onClick={handleRagStatus}
            disabled={kbBusy}
            className="px-3 py-1 rounded text-white text-sm"
            style={{ backgroundColor: theme.colors.primaryColor }}
          >
            Check Status
          </button>
          <button
            onClick={handleAttachDocs}
            disabled={kbBusy || convaiWrite === false}
            className="px-3 py-1 rounded text-white text-sm"
            style={{ backgroundColor: theme.colors.primaryColor }}
          >
            Attach to Agent
          </button>
          <button
            onClick={handleListAgentKb}
            disabled={kbBusy}
            className="px-3 py-1 rounded text-white text-sm"
            style={{ backgroundColor: theme.colors.primaryColor }}
          >
            List Agent KB
          </button>
          <button
            onClick={handleDetachDocs}
            disabled={kbBusy || convaiWrite === false}
            className="px-3 py-1 rounded text-white text-sm"
            style={{ backgroundColor: theme.colors.primaryColor }}
          >
            Detach Docs
          </button>
          <button
            onClick={handleAgentKbSize}
            disabled={kbBusy}
            className="px-3 py-1 rounded text-white text-sm"
            style={{ backgroundColor: theme.colors.primaryColor }}
          >
            Agent KB Size
          </button>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="text-xs">
            <span className="font-semibold">Resolved Agent ID:</span>{' '}
            <span className="font-mono">{agentId || '—'}</span>
            {autoAgentId && (
              <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded bg-green-100 text-green-700">
                auto
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs">
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={autoPoll}
                onChange={(e) => setAutoPoll(e.target.checked)}
              />
              Auto Poll
            </label>
            <span className="text-gray-500">
              Last update: {kbLastUpdated ? new Date(kbLastUpdated).toLocaleTimeString() : '—'}
            </span>
          </div>
        </div>

        {/* Live status/output */}
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="p-3 rounded" style={{ backgroundColor: theme.colors.bgPage, border: `1px solid ${theme.colors.borderColor}` }}>
            <div className="text-xs font-semibold mb-1" style={{ color: theme.colors.textSecondary }}>RAG Status</div>
            <pre className="text-xs overflow-auto" style={{ color: theme.colors.textPrimary, maxHeight: 160 }}>
{JSON.stringify(kbStatus, null, 2)}
            </pre>
          </div>
          <div className="p-3 rounded" style={{ backgroundColor: theme.colors.bgPage, border: `1px solid ${theme.colors.borderColor}` }}>
            <div className="text-xs font-semibold mb-1" style={{ color: theme.colors.textSecondary }}>Agent KB Size</div>
            <pre className="text-xs overflow-auto" style={{ color: theme.colors.textPrimary, maxHeight: 160 }}>
{JSON.stringify(agentKbSize, null, 2)}
            </pre>
          </div>
          <div className="p-3 rounded" style={{ backgroundColor: theme.colors.bgPage, border: `1px solid ${theme.colors.borderColor}` }}>
            <div className="text-xs font-semibold mb-1" style={{ color: theme.colors.textSecondary }}>Agent Docs</div>
            <pre className="text-xs overflow-auto" style={{ color: theme.colors.textPrimary, maxHeight: 160 }}>
{JSON.stringify(agentDocs, null, 2)}
            </pre>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div
          className="p-4 rounded-lg"
          style={{
            backgroundColor: theme.colors.bgCard,
            border: `1px solid ${theme.colors.borderColor}`,
          }}
        >
          <div className="text-2xl font-bold" style={{ color: theme.colors.primaryColor }}>
            {articles.length}
          </div>
          <div className="text-sm" style={{ color: theme.colors.textSecondary }}>
            Total Articles
          </div>
        </div>

        <div
          className="p-4 rounded-lg"
          style={{
            backgroundColor: theme.colors.bgCard,
            border: `1px solid ${theme.colors.borderColor}`,
          }}
        >
          <div className="text-2xl font-bold" style={{ color: theme.colors.successText }}>
            {articles.filter((a) => a.is_active).length}
          </div>
          <div className="text-sm" style={{ color: theme.colors.textSecondary }}>
            Active
          </div>
        </div>

        <div
          className="p-4 rounded-lg"
          style={{
            backgroundColor: theme.colors.bgCard,
            border: `1px solid ${theme.colors.borderColor}`,
          }}
        >
          <div className="text-2xl font-bold" style={{ color: theme.colors.primaryColor }}>
            {categories.length}
          </div>
          <div className="text-sm" style={{ color: theme.colors.textSecondary }}>
            Categories
          </div>
        </div>

        <div
          className="p-4 rounded-lg"
          style={{
            backgroundColor: theme.colors.bgCard,
            border: `1px solid ${theme.colors.borderColor}`,
          }}
        >
          <div className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
            {[...new Set(articles.flatMap((a) => a.tags || []))].length}
          </div>
          <div className="text-sm" style={{ color: theme.colors.textSecondary }}>
            Unique Tags
          </div>
        </div>
      </div>

      {Object.keys(articlesByCategory).length > 0 ? (
        <div className="space-y-6">
          {Object.entries(articlesByCategory).map(([categoryName, categoryArticles]) => (
            <div key={categoryName}>
              <div className="flex items-center space-x-2 mb-4">
                <FolderOpen className="w-5 h-5" style={{ color: theme.colors.primaryColor }} />
                <h2 className="text-lg font-semibold" style={{ color: theme.colors.textPrimary }}>
                  {categoryName}
                </h2>
                <span
                  className="px-2 py-1 text-xs rounded-full"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textSecondary,
                  }}
                >
                  {categoryArticles.length}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryArticles.map((article) => (
                  <div
                    key={article.id}
                    className="rounded-lg p-4"
                    style={{
                      backgroundColor: theme.colors.bgCard,
                      border: `1px solid ${theme.colors.borderColor}`,
                      opacity: article.is_active ? 1 : 0.6,
                    }}
                  >
                    <div className="flex-1">
                      <h3 className="text-base font-semibold mb-1" style={{ color: theme.colors.textPrimary }}>
                        {article.title}
                      </h3>
                      {article.summary && (
                        <p className="text-sm line-clamp-2" style={{ color: theme.colors.textSecondary }}>
                          {article.summary}
                        </p>
                      )}
                    </div>

                    <div className="mt-3 space-y-2">
                      <div className="flex items-center space-x-2 text-xs" style={{ color: theme.colors.textSecondary }}>
                        <FileText className="w-3 h-3" />
                        <span className="capitalize">{article.article_type}</span>
                        <span>•</span>
                        <span>Priority: {article.priority}</span>
                      </div>

                      {article.tags && article.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {article.tags.slice(0, 3).map((tag, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 text-xs rounded-full"
                              style={{
                                backgroundColor: theme.colors.bgPage,
                                color: theme.colors.textSecondary,
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                          {article.tags.length > 3 && (
                            <span className="text-xs" style={{ color: theme.colors.textTertiary }}>
                              +{article.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          className="text-center py-12"
          style={{
            backgroundColor: theme.colors.bgCard,
            border: `1px solid ${theme.colors.borderColor}`,
            borderRadius: '8px',
          }}
        >
          <FileText className="w-12 h-12 mx-auto mb-4" style={{ color: theme.colors.textTertiary }} />
          <p className="text-lg font-medium mb-2" style={{ color: theme.colors.textPrimary }}>
            No articles found
          </p>
          <p style={{ color: theme.colors.textSecondary }}>
            Get started by importing FAQs or adding your first article
          </p>
        </div>
      )}
    </div>
  );
}
